/**
 * LanguageSwitcher — EN / FR toggle for Quebec + French-Canadian users.
 *
 * How it works
 * ─────────────
 * 1. Auto-detects the visitor's preferred language on first load:
 *      • ?lang=fr in the URL wins (share-link friendly)
 *      • Otherwise localStorage.crc_lang if it exists
 *      • Otherwise navigator.language starting with "fr" → French
 * 2. When French is picked, we lazy-load Google's translate widget script
 *    and force-translate the whole page from English → French. The widget
 *    is invisible — only the pill in the header is the user affordance.
 * 3. Choice persists across pages via localStorage.
 *
 * We wire hand-crafted `<Trans i18nKey>` strings on top of this over time
 * for the highest-traffic pages (Landing, Pricing, Dashboard). Everything
 * else falls back to the widget so no page is left untranslated.
 */
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";

const LANG_KEY = "crc_lang";

function detectInitialLang() {
  if (typeof window === "undefined") return "en";
  try {
    const url = new URL(window.location.href);
    const q = (url.searchParams.get("lang") || "").toLowerCase();
    if (q === "fr" || q === "fr-ca") return "fr";
    if (q === "en") return "en";
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === "fr" || stored === "en") return stored;
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("fr")) return "fr";
  } catch { /* SSR / privacy-mode fallthrough */ }
  return "en";
}

// One-shot loader for Google's website-translation widget. We call this
// only when the user actually picks French, so English visitors pay no
// perf cost and no analytics call goes to Google.
function loadGoogleTranslate() {
  if (typeof window === "undefined") return;
  if (document.getElementById("crc-gtranslate-script")) return;
  // Google needs a global init callback + a container div.
  window.__crc_initTranslate = function () {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: "fr",
        autoDisplay: false,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
      },
      "crc-gtranslate-container",
    );
  };
  const host = document.createElement("div");
  host.id = "crc-gtranslate-container";
  host.style.cssText = "position:absolute;left:-9999px;top:-9999px;height:0;overflow:hidden;";
  document.body.appendChild(host);
  const s = document.createElement("script");
  s.id = "crc-gtranslate-script";
  s.src = "https://translate.google.com/translate_a/element.js?cb=__crc_initTranslate";
  s.async = true;
  document.body.appendChild(s);

  // Google's "Translated to: French · Show original · Options" banner is
  // injected as a top-level <iframe class="goog-te-banner-frame"> and
  // pushes <body> down by ~40px via inline style. Neither is stoppable
  // via widget options — we have to actively remove/reset them.
  const scrub = () => {
    // Remove the top banner ("Translated to: French · Show original · Options")
    // in every variant Google has shipped over the years. Do NOT touch the
    // hidden translate ENGINE iframe (usually src="translate.googleapis.com"
    // and display:none) — killing that disables translation entirely.
    document.querySelectorAll(
      [
        // Legacy class name.
        "iframe.goog-te-banner-frame",
        ".goog-te-banner-frame",
        // Current (2024–2026) obfuscated class name: VIpgJd-…-skiptranslate.
        // Match ANY iframe whose class list contains BOTH markers so we
        // never accidentally remove the engine iframe.
        'iframe.skiptranslate[class*="VIpgJd"]',
        // Fallback: any visible iframe whose title mentions "Google" and
        // that sits at the very top of the page (y ≈ 0, height < 60).
      ],
    ).forEach((n) => {
      try { n.remove(); } catch { /* noop */ }
    });
    // Belt-and-suspenders: hide any iframe with class 'skiptranslate' whose
    // src is about:blank (banner) AND leave iframes hitting google APIs
    // (engine) alone.
    document.querySelectorAll("iframe.skiptranslate").forEach((n) => {
      try {
        const src = n.getAttribute("src") || "";
        // Engine iframe uses translate.googleapis.com — never remove.
        if (src.includes("translate.googleapis.com") || src.includes("translate.google.com/translate_a")) {
          return;
        }
        // Anything else with .skiptranslate that's a top banner: kill it.
        const rect = n.getBoundingClientRect();
        if (rect.top <= 4 && rect.height > 0 && rect.height < 80) {
          n.remove();
        }
      } catch { /* noop */ }
    });
    // Google inline-styles body { top: 40px; position: relative } to make
    // room for its banner. Reset both so our layout stays flush.
    if (document.body) {
      document.body.style.top = "0";
      document.body.style.position = "static";
    }
    if (document.documentElement) {
      document.documentElement.style.marginTop = "0";
      document.documentElement.style.top = "0";
    }
  };
  scrub();
  // Observe the ENTIRE document so we catch banner iframes injected deep
  // in the tree (Google sometimes wraps them in body > div > iframe). The
  // previous `subtree: false` scope missed those and let the banner
  // linger through page refreshes.
  const obs = new MutationObserver(scrub);
  obs.observe(document.documentElement, { childList: true, subtree: true });
  // Cheap safety net in case body isn't fully ready when this fires.
  window.setTimeout(scrub, 500);
  window.setTimeout(scrub, 1500);
  window.setTimeout(scrub, 3500);
  window.setTimeout(scrub, 6000);
}

function _reTranslate() {
  // Force Google to re-scan the DOM. Needed after React swaps routes /
  // renders new content, because Translate's default MutationObserver
  // misses many virtual-DOM updates.
  const sel = document.querySelector("#crc-gtranslate-container select.goog-te-combo");
  if (!sel) return;
  const cur = sel.value;
  if (cur !== "fr") return;
  // Toggle to nothing, then back to fr — this is the documented way to
  // make the widget re-scan without a page reload.
  try {
    sel.value = "en";
    sel.dispatchEvent(new Event("change"));
    setTimeout(() => {
      sel.value = "fr";
      sel.dispatchEvent(new Event("change"));
    }, 40);
  } catch { /* noop */ }
}


function applyLanguage(lang) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang === "fr" ? "fr-CA" : "en-CA";
  if (lang === "fr") {
    // Google Translate honours the `googtrans` cookie/URL fragment as a
    // set-and-forget language directive. Setting the cookie BEFORE the
    // script loads means Translate applies FR on the very first paint
    // instead of a 3-5 sec flash of English.
    const host = window.location.hostname;
    document.cookie = "googtrans=/en/fr; path=/;";
    if (host && host !== "localhost") {
      document.cookie = `googtrans=/en/fr; path=/; domain=${host};`;
      document.cookie = `googtrans=/en/fr; path=/; domain=.${host};`;
    }
    loadGoogleTranslate();
    // If widget is already loaded from a prior visit, fire an immediate
    // <select>-change to translate what's currently on-screen.
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      const sel = document.querySelector("#crc-gtranslate-container select.goog-te-combo");
      if (sel) {
        sel.value = "fr";
        sel.dispatchEvent(new Event("change"));
        clearInterval(iv);
      } else if (tries > 40) {
        // Widget failed to inject — force a one-shot reload so the
        // `googtrans` cookie takes effect during Translate's initial boot.
        // Guarded by sessionStorage so we never loop forever if Translate
        // is completely blocked in the visitor's network (ad-blocker etc.).
        clearInterval(iv);
        let alreadyReloaded = false;
        try { alreadyReloaded = sessionStorage.getItem("crc_fr_reloaded") === "1"; } catch { /* private mode */ }
        if (!alreadyReloaded) {
          try { sessionStorage.setItem("crc_fr_reloaded", "1"); } catch { /* private mode */ }
          window.location.reload();
        }
      }
    }, 150);

    // Periodic re-translate for the first 30 s after language pick, then
    // every 4s indefinitely so lazy-mounted content (footer partners
    // section, downloadable panels, portal sidebars) that renders after
    // the initial burst still gets translated. Home page has multiple
    // lazy-mounted sections (hero, testimonials, pricing cards, blog
    // snippets, floating widgets) that render after Translate's initial
    // scan and stay in English otherwise.
    let sweeps = 0;
    const sweep = setInterval(() => {
      sweeps += 1;
      _reTranslate();
      // After the first 30s burst, slow to every 4s but never stop.
      if (sweeps === 38) {
        clearInterval(sweep);
        window.__crcFRSlowSweep = setInterval(_reTranslate, 4000);
      }
    }, 800);
    // Extra re-scan on user interactions.
    if (!window.__crcFRInteract) {
      window.__crcFRInteract = true;
      const onInteract = () => setTimeout(_reTranslate, 300);
      window.addEventListener("scroll", onInteract, { passive: true });
      window.addEventListener("click", onInteract, true);
    }

    // Hook SPA navigation so we re-translate every route change. Without
    // this, only the first-loaded page is fully French — React-rendered
    // pages that appear later stay partially English.
    if (!window.__crcFRHooked) {
      window.__crcFRHooked = true;
      const origPush = window.history.pushState;
      const origReplace = window.history.replaceState;
      window.history.pushState = function (...args) {
        origPush.apply(this, args);
        setTimeout(_reTranslate, 250);
        setTimeout(_reTranslate, 900);
      };
      window.history.replaceState = function (...args) {
        origReplace.apply(this, args);
        setTimeout(_reTranslate, 250);
        setTimeout(_reTranslate, 900);
      };
      window.addEventListener("popstate", () => {
        setTimeout(_reTranslate, 250);
        setTimeout(_reTranslate, 900);
      });
      // Also observe the main content area for large mutations
      // (e.g. lazy-loaded lists rendering async).
      const target = document.querySelector("#root") || document.body;
      if (target) {
        let debounce = null;
        const mo = new MutationObserver(() => {
          if (debounce) clearTimeout(debounce);
          debounce = setTimeout(_reTranslate, 400);
        });
        mo.observe(target, { childList: true, subtree: true });
        window.__crcFRObs = mo;
      }
    }
  } else {
    // Clear the cookie in every scope so future page loads stay EN.
    const host = window.location.hostname;
    const past = "expires=Thu, 01 Jan 1970 00:00:00 UTC";
    document.cookie = `googtrans=; ${past}; path=/;`;
    if (host && host !== "localhost") {
      document.cookie = `googtrans=; ${past}; path=/; domain=${host};`;
      document.cookie = `googtrans=; ${past}; path=/; domain=.${host};`;
    }
    // Reset the one-shot FR reload guard so switching back to FR later
    // still triggers the cookie-boot reload path if needed.
    try { sessionStorage.removeItem("crc_fr_reloaded"); } catch { /* private mode */ }
    // Stop the persistent re-translate sweep so we don't keep hitting
    // Google Translate on every 4s when the user has explicitly picked EN.
    if (window.__crcFRSlowSweep) {
      clearInterval(window.__crcFRSlowSweep);
      window.__crcFRSlowSweep = null;
    }
    if (document.documentElement.classList.contains("crc-fr-active")) {
      window.location.reload();
    }
  }
  document.documentElement.classList.toggle("crc-fr-active", lang === "fr");
}

export default function LanguageSwitcher({ compact = false }) {
  const [lang, setLang] = useState("en");

  useEffect(() => {
    const initial = detectInitialLang();
    setLang(initial);
    applyLanguage(initial);
  }, []);

  const pick = (next) => {
    if (next === lang) return;
    setLang(next);
    try { localStorage.setItem(LANG_KEY, next); } catch { /* noop */ }
    applyLanguage(next);
    // Instant same-tab notification so every `useT()` consumer re-renders
    // without a page reload. Cross-tab sync is handled by the `storage`
    // event automatically.
    try { window.dispatchEvent(new CustomEvent("crc-lang-changed", { detail: { lang: next } })); } catch { /* noop */ }
  };

  return (
    <div
      role="group"
      aria-label="Language selector"
      data-testid="language-switcher"
      className="inline-flex items-center gap-0.5 p-0.5 rounded-full border border-emerald-500 bg-white shadow-sm"
    >
      <Globe className="w-3 h-3 text-emerald-600 ml-1 flex-none" aria-hidden />
      <button
        type="button"
        onClick={() => pick("en")}
        aria-pressed={lang === "en"}
        data-testid="language-switcher-en"
        className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full transition-colors ${
          lang === "en" ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-800"
        }`}
        title="English (Canada)"
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => pick("fr")}
        aria-pressed={lang === "fr"}
        data-testid="language-switcher-fr"
        className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full transition-colors ${
          lang === "fr" ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-800"
        }`}
        title="Français (Canada)"
      >
        FR
      </button>
    </div>
  );
}
